import { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";

const emptyForm = { title: "", description: "", display_order: 0 };

function FacilitiesPage() {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [iconFile, setIconFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {
      const res = await axiosInstance.get("/api/facilities");
      setFacilities(res.data);
    } catch (err) {
      toast.error("Failed to load facilities");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setIconFile(null);
    setShowModal(true);
  };

  const openEditModal = (facility) => {
    setEditingId(facility.id);
    setForm({
      title: facility.title,
      description: facility.description || "",
      display_order: facility.display_order,
    });
    setIconFile(null);
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("description", form.description);
    formData.append("display_order", form.display_order);
    if (iconFile) formData.append("icon", iconFile);

    try {
      if (editingId) {
        await axiosInstance.put(`/api/facilities/${editingId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Facility updated");
      } else {
        await axiosInstance.post("/api/facilities", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Facility added");
      }
      setShowModal(false);
      fetchFacilities();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save facility");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;

    try {
      await axiosInstance.delete(`/api/facilities/${id}`);
      toast.success("Facility deleted");
      fetchFacilities();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete facility");
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Facilities</h4>
        <button className="btn btn-primary" onClick={openAddModal}>
          <i className="bi bi-plus-lg me-1"></i> Add Facility
        </button>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: "70px" }}>Icon</th>
                <th>Title</th>
                <th className="d-none d-md-table-cell">Description</th>
                <th style={{ width: "80px" }}>Order</th>
                <th style={{ width: "120px" }} className="text-end">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {facilities.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-4">
                    No facilities added yet.
                  </td>
                </tr>
              ) : (
                facilities.map((f) => (
                  <tr key={f.id}>
                    <td>
                      {f.icon_path ? (
                        <img
                          src={`${process.env.REACT_APP_API_URL}/${f.icon_path}`}
                          alt={f.title}
                          className="rounded"
                          style={{
                            width: "44px",
                            height: "44px",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          className="bg-light rounded d-flex align-items-center justify-content-center"
                          style={{ width: "44px", height: "44px" }}
                        >
                          <i className="bi bi-image text-muted"></i>
                        </div>
                      )}
                    </td>
                    <td className="fw-medium">{f.title}</td>
                    <td className="d-none d-md-table-cell text-muted small">
                      {f.description || "—"}
                    </td>
                    <td>{f.display_order}</td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-outline-secondary me-1"
                        onClick={() => openEditModal(f)}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(f.id, f.title)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editingId ? "Edit Facility" : "Add Facility"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowModal(false)}
                    ></button>
                  </div>

                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={form.title}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        name="description"
                        rows="3"
                        value={form.description}
                        onChange={handleFormChange}
                      ></textarea>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Display Order</label>
                      <input
                        type="number"
                        className="form-control"
                        name="display_order"
                        value={form.display_order}
                        onChange={handleFormChange}
                      />
                    </div>

                    <div className="mb-1">
                      <label className="form-label">Icon Image</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".png,.jpg,.jpeg,.webp"
                        onChange={(e) => setIconFile(e.target.files[0])}
                      />
                      {editingId && (
                        <small className="text-muted">
                          Leave empty to keep the current icon
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? "Saving..." : editingId ? "Update" : "Add"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </>
      )}
    </div>
  );
}

export default FacilitiesPage;
